import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Search, Users, Store, Package, ShoppingCart, X, Clock, UserPlus, Warehouse, ClipboardCheck } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Static shortcuts — surface key pages (warehouses, stock-checks, receipts) in
// search even when the backend's /team/search doesn't index them.
const STATIC_SHORTCUTS = [
  // London stock-check shortcut removed 21 Apr 2026 — non-stockholding office.
  // See locked rule `stock_locations_single_source_of_truth`.
  { id: 'sc-crewe', type: 'shortcut', title: 'Crewe Stock Check', subtitle: 'Crewe Warehouse · Jane Beeston', url: '/dashboard/manual-stock-check?location=uk_warehouse', keywords: ['crewe','crewe stock','crewe warehouse','jane','crewe orlando','uk stock','uk warehouse','dispatch','stock','stock check','warehouse','uk','count'] },
  { id: 'sc-goodness', type: 'shortcut', title: 'Goodness Storage Stock Check', subtitle: 'Goodness · Lagos · rep stock', url: '/dashboard/manual-stock-check?location=nigeria_storage', keywords: ['goodness','goodness storage','nigeria rep','rep stock','goodness stock','lagos','nigeria'] },
  { id: 'sc-ng-main', type: 'shortcut', title: 'Main Nigeria Warehouse Stock Check', subtitle: 'Central NG · Lagos', url: '/dashboard/manual-stock-check?location=nigeria_main_warehouse', keywords: ['main nigeria','main ng','nigeria warehouse','central nigeria','nigeria main','lagos main','ng main','nigeria central'] },
  { id: 'sc-manual', type: 'shortcut', title: 'Manual Stock Check', subtitle: 'Choose any warehouse', url: '/dashboard/manual-stock-check', keywords: ['stock','stock check','count','warehouse','manual','inventory'] },
  { id: 'sc-receipts-review', type: 'shortcut', title: 'Payment Receipt Review', subtitle: 'CEO receipt verification', url: '/dashboard/payment-review', keywords: ['receipt','receipts','payment','payments','review','approve'] },
  { id: 'sc-invoices', type: 'shortcut', title: 'Invoices', subtitle: 'All invoices', url: '/dashboard/admin?tab=invoices', keywords: ['invoice','invoices','bill','billing'] },
  { id: 'sc-orders', type: 'shortcut', title: 'Orders', subtitle: 'All orders', url: '/dashboard/admin?tab=orders', keywords: ['order','orders','dispatch','shipping'] },
  { id: 'sc-salons', type: 'shortcut', title: 'Salon Pipeline', subtitle: 'Salon CRM & pipeline', url: '/dashboard/salon-pipeline', keywords: ['salon','salons','pipeline','crm','directory'] },
  { id: 'sc-reps', type: 'shortcut', title: 'Sales Reps', subtitle: 'Rep leaderboard & management', url: '/dashboard/admin?tab=sales-command', keywords: ['rep','reps','sales rep','goodness','ronke','leaderboard','commission'] },
];

const TYPE_ICONS = {
  rep: Users,
  salon: Store,
  product: Package,
  order: ShoppingCart,
  shortcut: ClipboardCheck,
};

const TYPE_LABELS = {
  rep: 'People',
  salon: 'Salons',
  product: 'Products',
  order: 'Orders',
  shortcut: 'Shortcuts',
};

const TYPE_COLORS = {
  rep: 'bg-blue-100 text-blue-600',
  salon: 'bg-emerald-100 text-emerald-600',
  product: 'bg-amber-100 text-amber-600',
  order: 'bg-purple-100 text-purple-600',
  shortcut: 'bg-black text-[#B99A65]',
};

export default function GlobalSearch({ autoFocus = false }) {
  const { token, user } = useAuth();
  const isSalon = ['salon', 'salon_owner', 'salon_manager', 'salon_staff', 'salon_business', 'public_customer', 'team_member', 'professional'].includes(user?.role);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const [recents, setRecents] = useState([]);

  // Load recent searches from localStorage
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('rk_recent_searches') || '[]');
      setRecents(stored.slice(0, 5));
    } catch { /* silent */ }
  }, []);

  const saveRecent = (item) => {
    try {
      const stored = JSON.parse(localStorage.getItem('rk_recent_searches') || '[]');
      const filtered = stored.filter(s => s.id !== item.id);
      const updated = [{ id: item.id, title: item.title, type: item.type, url: item.url }, ...filtered].slice(0, 5);
      localStorage.setItem('rk_recent_searches', JSON.stringify(updated));
      setRecents(updated);
    } catch { /* silent */ }
  };

  const fetchResults = useCallback(async () => {
    if (!query || query.length < 1) { setResults({}); return; }
    setLoading(true);
    // Client-side shortcut matches (warehouse/stock-check/receipts/etc)
    const q = query.toLowerCase().trim();
    const matches = (isSalon ? [] : STATIC_SHORTCUTS).filter(s =>
      s.title.toLowerCase().includes(q) ||
      s.subtitle.toLowerCase().includes(q) ||
      s.keywords.some(k => k.includes(q) || q.includes(k))
    );
    try {
      const res = await axios.get(`${API}/team/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      let cats = res.data.categories || {};
      if (isSalon) cats = cats.product ? { product: cats.product } : (cats.products ? { products: cats.products } : {});
      if (matches.length > 0) cats.shortcut = matches;
      setResults(cats);
      setSelectedIndex(-1);
    } catch {
      // Backend failed — still surface shortcuts
      setResults(matches.length ? { shortcut: matches } : {});
    }
    setLoading(false);
  }, [query, token]);

  useEffect(() => {
    const timer = setTimeout(fetchResults, 150);
    return () => clearTimeout(timer);
  }, [fetchResults]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) && inputRef.current && !inputRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allResults = Object.values(results).flat();

  const handleSelect = (item) => {
    saveRecent(item);
    navigate(item.url);
    setQuery('');
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, allResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0 && allResults[selectedIndex]) {
      handleSelect(allResults[selectedIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const hasResults = allResults.length > 0;
  const showDropdown = open && (hasResults || query.length >= 1 || recents.length > 0);

  return (
    <div className="relative w-full max-w-md" data-testid="global-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/50" />
        <input
          autoFocus={autoFocus}
          ref={inputRef}
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={isSalon ? 'Search products' : 'Search salons, orders, products, stock check…'}
          className="w-full h-9 pl-9 pr-8 rounded-lg border border-border/50 bg-[#F4F8FC] text-sm focus:outline-none focus:ring-2 focus:ring-[#B99A65]/30 focus:border-[#B99A65]/50 transition-all placeholder:text-muted-foreground/40"
          data-testid="global-search-input"
        />
        {query && (
          <button className="absolute right-2.5 top-1/2 -translate-y-1/2" onClick={() => { setQuery(''); setResults({}); }}>
            <X className="w-3.5 h-3.5 text-muted-foreground/50" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-border/40 rounded-xl shadow-lg max-h-[400px] overflow-y-auto z-50"
          data-testid="search-dropdown">
          
          {/* Recent Searches (shown when no query) */}
          {!query && recents.length > 0 && (
            <div className="p-2">
              <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-1">
                <Clock className="w-3 h-3" /> Recent
              </div>
              {recents.map((item, i) => {
                const Icon = TYPE_ICONS[item.type] || Search;
                return (
                  <button key={item.id + i}
                    className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-[#F4F8FC] transition-colors text-left"
                    onClick={() => handleSelect(item)}
                    data-testid={`recent-${i}`}>
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${TYPE_COLORS[item.type] || 'bg-gray-100 text-gray-500'}`}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-sm truncate">{item.title}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Search Results */}
          {query.length >= 1 && (
            loading ? (
              <div className="p-4 text-center text-xs text-muted-foreground">Searching...</div>
            ) : hasResults ? (
              <div className="p-2">
                {Object.entries(results).map(([type, items]) => (
                  <div key={type} className="mb-1">
                    <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                      {TYPE_LABELS[type] || type}
                    </div>
                    {items.map((item, i) => {
                      const Icon = TYPE_ICONS[type] || Search;
                      const globalIdx = allResults.indexOf(item);
                      return (
                        <button key={item.id + i}
                          className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg transition-colors text-left ${
                            globalIdx === selectedIndex ? 'bg-[#B99A65]/10' : 'hover:bg-[#F4F8FC]'
                          }`}
                          onClick={() => handleSelect(item)}
                          data-testid={`result-${type}-${i}`}>
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 overflow-hidden ${
                            item.image ? '' : TYPE_COLORS[type] || 'bg-gray-100 text-gray-500'
                          }`}>
                            {item.image ? (
                              <img src={item.image} alt="" className="w-full h-full object-cover rounded-lg" />
                            ) : (
                              <Icon className="w-4 h-4" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{item.title}</div>
                            <div className="text-[10px] text-muted-foreground truncate">{item.subtitle}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-3">
                <div className="text-center text-xs text-muted-foreground mb-2">No results for "{query}"</div>
                <button
                  onClick={() => { navigate(`/dashboard/salon-pipeline?invite=${encodeURIComponent(query)}`); setQuery(''); setOpen(false); }}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-[#B99A65]/10 hover:bg-[#B99A65]/20 transition-colors text-sm font-medium text-[#B99A65]"
                  data-testid="invite-from-search"
                >
                  <UserPlus className="w-4 h-4" />
                  + Invite "{query}" to join Revitalise Keratin
                </button>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}
